import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@datainsights.io");
  const [password, setPassword] = useState("Admin@12345");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { user, company } = await login(email, password);
      toast.success(`Welcome back, ${user.name}`);
      nav(`/t/${company.slug}`);
    } catch (err) {
      const d = err?.response?.data?.detail || err.message;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-[var(--bg)]">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden bg-slate-900 text-white">
        <div className="grid-bg absolute inset-0 opacity-20" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 20% 30%, rgba(37,99,235,0.35), transparent 55%), radial-gradient(circle at 80% 70%, rgba(124,58,237,0.28), transparent 50%)" }} />
        <div className="relative z-10 p-14 flex flex-col justify-between w-full">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-white text-slate-900 flex items-center justify-center font-bold">P</div>
            <span className="font-display text-lg font-semibold tracking-tight">PeopleIQ</span>
          </div>
          <div>
            <div className="chip chip-primary mb-6" style={{ background: "rgba(255,255,255,0.1)", color: "white" }}>
              <Sparkles size={12} /> AI-native People Analytics
            </div>
            <h1 className="font-display text-5xl xl:text-[64px] leading-[1.03] tracking-tight mb-6">
              Your People Analytics<br />team, <span className="text-blue-400">powered by AI.</span>
            </h1>
            <p className="text-slate-300 max-w-md leading-relaxed text-lg">
              Connect your HR data. Ask questions in plain English. Get decision-ready workforce insights in seconds.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-xl">
              <Cell title="Ask" body="Natural-language questions about your workforce." />
              <Cell title="Analyze" body="Auto-generated dashboards and insights." />
              <Cell title="Act" body="Recommendations for the CHRO's next move." />
            </div>
          </div>
          <div className="text-xs text-slate-500 mono">PeopleIQ · Multi-tenant SaaS · v1.0</div>
        </div>
      </div>

      {/* Right form */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="label-caps mb-2">Sign in</div>
            <h2 className="font-display text-3xl font-medium tracking-tight text-slate-900">Welcome back</h2>
            <p className="text-sm text-slate-500 mt-2">Access your company's PeopleIQ workspace.</p>
          </div>
          <div className="space-y-4">
            <div>
              <label className="label-caps block mb-2">Work email</label>
              <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-base" required />
            </div>
            <div>
              <label className="label-caps block mb-2">Password</label>
              <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input-base" required />
            </div>
          </div>
          <button data-testid="login-submit" type="submit" disabled={loading} className="btn-primary w-full justify-center">
            {loading ? "Signing in…" : <>Sign in <ArrowRight size={14} /></>}
          </button>
          <div className="text-sm text-slate-500 text-center">
            New to PeopleIQ? <Link to="/signup" data-testid="link-signup" className="text-[var(--primary)] font-medium hover:underline">Create a workspace</Link>
          </div>
          <div className="pt-6 border-t border-slate-200 text-xs text-slate-500 space-y-1">
            <div className="mono">Demo admin: admin@datainsights.io / Admin@12345</div>
            <div className="mono">Demo viewer: user@datainsights.io / User@12345</div>
          </div>
        </form>
      </div>
    </div>
  );
}

function Cell({ title, body }) {
  return (
    <div className="rounded-lg border border-white/10 p-4 bg-white/[0.02] backdrop-blur">
      <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">{title}</div>
      <div className="text-sm text-slate-300 leading-snug">{body}</div>
    </div>
  );
}
